import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Card from '@/components/base/Card';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import { formatCurrency } from '@/lib/format';
import type { OutstandingDues } from '@/types';

interface OutstandingSectionProps {
  data: OutstandingDues | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onRequest: () => void;
}

export default function OutstandingSection({
  data,
  loading,
  error,
  onRetry,
  onRequest,
}: OutstandingSectionProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Card className="space-y-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-20 w-full" />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <ErrorState
          title={t('finance.outstanding.error')}
          description={t('common.errorText')}
          retryLabel={t('common.retry')}
          onRetry={onRetry}
        />
      </Card>
    );
  }

  const hasBalance = data.total > 0;

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="border-b border-background-200 px-4 py-4 md:px-5">
        <h2 className="text-base font-semibold text-foreground-950">{t('finance.outstanding.title')}</h2>
        <p className="mt-0.5 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
          <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
          {t('finance.outstanding.note')}
        </p>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-background-100 bg-background-100/60 px-4 py-5 md:px-5">
        <div>
          <p className="text-sm text-foreground-500">{t('finance.outstanding.balanceLabel')}</p>
          <p className="mt-1.5 text-3xl font-bold text-foreground-950">
            {formatCurrency(data.total, data.currency)}
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          icon="ri-send-plane-line"
          disabled={!hasBalance}
          onClick={onRequest}
        >
          {t('finance.outstanding.request')}
        </Button>
      </div>

      {hasBalance ? (
        <div className="px-4 py-4 md:px-5">
          <h3 className="mb-3 text-sm font-semibold text-foreground-800">
            {t('finance.outstanding.breakdown')}
          </h3>
          <ul className="divide-y divide-background-100 rounded-lg border border-background-200">
            {data.components.map((component) => (
              <li key={component.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  <i className="ri-file-list-3-line text-base text-foreground-400" aria-hidden="true" />
                  <span className="text-sm text-foreground-700">
                    {t('finance.outstanding.orderComponent')}
                  </span>
                  <Link
                    to={`/dashboard/orders/${component.orderId ?? ''}`}
                    className="lb-ltr-field text-sm font-medium text-primary-700 hover:underline"
                  >
                    {component.label}
                  </Link>
                </div>
                <span className="whitespace-nowrap text-sm font-semibold text-foreground-900">
                  {formatCurrency(component.amount, data.currency)}
                </span>
              </li>
            ))}
          </ul>

          {data.packagePending ? (
            <p className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs leading-relaxed text-amber-800">
              <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true" />
              {t('finance.outstanding.packagePending')}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="p-4 md:p-5">
          <EmptyState
            icon="ri-checkbox-circle-line"
            title={t('finance.outstanding.empty')}
            description={t('finance.outstanding.emptyDesc')}
          />
        </div>
      )}
    </Card>
  );
}